export type RouteToComponentMapper = [string, string];

export type Paths = RouteToComponentMapper[];

/**
 * Checks if a current route matches any of the provided path patterns.
 * This function handles route parameters by stripping them from the pattern before comparison.
 * 
 * @param paths - Array of route-to-component mappings, where each tuple contains [routePattern, componentName]
 * @param route - The current route string to check against the patterns
 * @returns true if the current route starts with any of the path patterns, false otherwise
 * 
 * @example
 * const dashboardPaths = [
 *   ['/dashboard', 'Dashboard'],
 *   ['/settings/:id', 'Settings'],
 *   ['/profile', 'Profile']
 * ];
 * 
 * isCurrentRoutePartOf(dashboardPaths, '/dashboard/overview') // returns true
 * isCurrentRoutePartOf(dashboardPaths, '/settings/user-preferences') // returns true 
 * isCurrentRoutePartOf(dashboardPaths, '/profile') // returns true
 * isCurrentRoutePartOf(dashboardPaths, '/login') // returns false
 * isCurrentRoutePartOf(dashboardPaths, '') // returns false
 */
export const isCurrentRoutePartOf = (paths: Paths, route: string) => {
	// Handle empty route
	if (!route) {
		return false;
	}
	
	return paths.some((path) => {
		// Extract the first segment after the first '/', handling route parameters
		const getFirstSegment = (str: string) => {
			const withoutParams = str.split(':')[0]; // Remove route parameters
			const segments = withoutParams.split('/');
			return segments[1] || '';  // segments[0] is empty for strings starting with '/'
		};
		
		const routeFirstSegment = getFirstSegment(route);
		const pathFirstSegment = getFirstSegment(path[0]);
		
		return routeFirstSegment === pathFirstSegment;
	});
};
